import { useMemo, useState } from 'react';
import { Modal } from '../ui/Modal';
import { MoneyAmount } from './MoneyAmount';
import {
  docAmountNetOfCm,
  matchesCollectionSearch,
  sortCollectionDocs,
  type AccountCollectionDocument,
  type CollectionDocSortKey,
} from '../../lib/accountCollection';
import { formatSingaporeDate } from '../../utils/dateTime';

function formatMoneyPlain(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return value.toLocaleString(undefined, {
    style: 'currency',
    currency: 'SGD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return formatSingaporeDate(value);
}

function docTypeLabel(documentType: string): string {
  const t = documentType.trim();
  switch (t) {
    case 'Credit Memo':
      return 'CM';
    case 'Reclass':
      return 'RCL';
    case 'Payment':
      return 'PMT';
    case '':
      return 'RCL';
    default:
      return t === 'Invoice' ? 'INV' : t.slice(0, 3).toUpperCase();
  }
}

function statusLabel(status: AccountCollectionDocument['due_status']): string {
  switch (status) {
    case 'overdue':
      return 'Overdue';
    case 'paid':
      return 'Paid';
    case 'waived':
      return 'Waived';
    case 'applied':
      return 'Applied';
    case 'reclass':
      return 'Reclass';
    case 'adjustment':
      return 'Adjust';
    case 'credit':
      return 'Credit';
    default:
      return 'Not due';
  }
}

function isClosedDocStatus(status: AccountCollectionDocument['due_status']): boolean {
  return (
    status === 'paid' ||
    status === 'waived' ||
    status === 'applied' ||
    status === 'credit'
  );
}

function SortTh({
  label,
  sortKey,
  activeKey,
  dir,
  numeric,
  onSort,
}: {
  label: string;
  sortKey: CollectionDocSortKey;
  activeKey: CollectionDocSortKey;
  dir: 'asc' | 'desc';
  numeric?: boolean;
  onSort: (key: CollectionDocSortKey) => void;
}) {
  const active = activeKey === sortKey;
  return (
    <th
      className={['account-collection__th--sort', numeric ? 'account-collection__num' : '']
        .filter(Boolean)
        .join(' ')}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        className="account-collection__sort-btn"
        onClick={() => onSort(sortKey)}
      >
        <span>{label}</span>
        <span className="account-collection__sort-ind" aria-hidden>
          {active ? (dir === 'asc' ? '▲' : '▼') : ''}
        </span>
      </button>
    </th>
  );
}

export function CollectionMonthDocumentsModal({
  title,
  documents,
  matchDoc,
  onClose,
}: {
  title: string;
  documents: AccountCollectionDocument[];
  matchDoc: (doc: AccountCollectionDocument) => boolean;
  onClose: () => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<CollectionDocSortKey>('due_status');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const rows = useMemo(() => {
    const filtered = documents.filter(
      (d) => matchDoc(d) && matchesCollectionSearch(d, searchQuery),
    );
    return sortCollectionDocs(filtered, sortKey, sortDir);
  }, [documents, matchDoc, searchQuery, sortKey, sortDir]);

  const listStats = useMemo(() => {
    let openOutstanding = 0;
    let openOverdue = 0;
    let openNotDue = 0;
    let openInv = 0;
    let paidAmt = 0;
    let invoicedAmt = 0;
    let cmOnPaid = 0;
    for (const d of rows) {
      if (isClosedDocStatus(d.due_status)) {
        paidAmt += d.collected_lcy || 0;
        if (d.document_type === 'Invoice') {
          const cm = Math.max(0, d.credit_applied_lcy || 0);
          invoicedAmt += Math.max(0, (d.invoice_lcy || 0) - cm);
          cmOnPaid += cm;
        }
        continue;
      }
      openOutstanding += d.remaining_lcy;
      if (d.due_status === 'overdue') openOverdue += d.remaining_lcy;
      else if (d.due_status === 'not_due') openNotDue += d.remaining_lcy;
      if (d.document_type === 'Invoice' && Math.abs(d.remaining_lcy) > 0.005) openInv += 1;
    }
    return {
      openOutstanding,
      openOverdue,
      openNotDue,
      openInv,
      paidAmt,
      invoicedAmt,
      cmOnPaid,
    };
  }, [rows]);

  function handleSort(key: CollectionDocSortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir(
      key === 'document_no' ||
        key === 'outlet_code' ||
        key === 'outlet_name' ||
        key === 'due_date' ||
        key === 'document_type' ||
        key === 'due_status'
        ? 'asc'
        : 'desc',
    );
  }

  return (
    <Modal
      title={title}
      open
      onClose={onClose}
      panelClassName="modal-panel--collection-detail"
    >
      <div className="account-collection-modal">
        <header className="account-collection-modal__header">
          <p className="account-collection-modal__meta">
            Open {formatMoneyPlain(listStats.openOutstanding)} · Overdue{' '}
            {formatMoneyPlain(listStats.openOverdue)} · Not due{' '}
            {formatMoneyPlain(listStats.openNotDue)} · Open inv {listStats.openInv}
            {listStats.invoicedAmt > 0.005
              ? listStats.cmOnPaid > 0.005
                ? ` · Invoiced ${formatMoneyPlain(listStats.invoicedAmt)} (net of CM ${formatMoneyPlain(listStats.cmOnPaid)}) · Paid ${formatMoneyPlain(listStats.paidAmt)}`
                : ` · Paid ${formatMoneyPlain(listStats.paidAmt)} / Invoiced ${formatMoneyPlain(listStats.invoicedAmt)}`
              : ''}
          </p>
          <div className="account-collection__search">
            <input
              type="search"
              className="account-collection__search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by document number or type"
              aria-label="Search documents"
            />
            {searchQuery.trim() ? (
              <button
                type="button"
                className="account-collection__search-clear"
                onClick={() => setSearchQuery('')}
              >
                Clear
              </button>
            ) : null}
          </div>
        </header>
        <div className="account-collection__table-wrap account-collection__table-wrap--modal">
          <table className="account-collection__table account-collection__table--docs">
            <colgroup>
              <col className="account-collection__col--doc" />
              <col className="account-collection__col--type" />
              <col className="account-collection__col--due" />
              <col className="account-collection__col--days" />
              <col className="account-collection__col--amt" />
              <col className="account-collection__col--status" />
            </colgroup>
            <thead>
              <tr>
                <SortTh
                  label="Doc No."
                  sortKey="document_no"
                  activeKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <SortTh
                  label="Type"
                  sortKey="document_type"
                  activeKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <SortTh
                  label="Due"
                  sortKey="due_date"
                  activeKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                />
                <SortTh
                  label="Days"
                  sortKey="days_past_due"
                  activeKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                  numeric
                />
                <SortTh
                  label="Amount"
                  sortKey="remaining_lcy"
                  activeKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                  numeric
                />
                <SortTh
                  label="Status"
                  sortKey="due_status"
                  activeKey={sortKey}
                  dir={sortDir}
                  onSort={handleSort}
                />
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="account-collection__empty">
                    No documents for this month.
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr
                    key={`${row.entry_no}-${row.document_no}`}
                    className={
                      row.due_status === 'overdue'
                        ? 'account-collection__row--overdue'
                        : row.due_status === 'paid'
                          ? 'account-collection__row--paid'
                          : row.due_status === 'waived' || row.due_status === 'applied'
                            ? 'account-collection__row--waived'
                            : row.due_status === 'credit'
                              ? 'account-collection__row--credit'
                              : undefined
                    }
                  >
                    <td>
                      <div className="account-collection__mono">{row.document_no}</div>
                      {row.description !== row.document_no ? (
                        <div className="account-collection__desc">{row.description}</div>
                      ) : null}
                      {row.linked_invoice_no ? (
                        <div className="account-collection__desc">→ {row.linked_invoice_no}</div>
                      ) : null}
                      {row.linked_cm_no ? (
                        <div className="account-collection__desc">← CM {row.linked_cm_no}</div>
                      ) : null}
                      {row.credit_alloc === 'posting_date' ? (
                        <div className="account-collection__desc">Allocated by posting date</div>
                      ) : null}
                    </td>
                    <td>{docTypeLabel(row.document_type)}</td>
                    <td className="account-collection__due">
                      {row.document_type === 'Credit Memo' &&
                      (row.linked_invoice_no ||
                        row.due_status === 'applied' ||
                        row.due_status === 'waived')
                        ? '—'
                        : formatDate(row.due_date)}
                    </td>
                    <td className="account-collection__num account-collection__days">
                      {row.due_status === 'paid' ||
                      row.due_status === 'credit' ||
                      row.due_status === 'waived' ||
                      row.due_status === 'applied' ||
                      (row.document_type === 'Credit Memo' && row.linked_invoice_no)
                        ? '—'
                        : row.days_past_due > 0
                          ? row.days_past_due
                          : '0'}
                    </td>
                    <td className="account-collection__num account-collection__amt-cell">
                      <MoneyAmount
                        value={docAmountNetOfCm(row)}
                        overdue={row.due_status === 'overdue'}
                      />
                      {row.due_status === 'paid' && row.credit_applied_lcy > 0.005 ? (
                        <div className="account-collection__desc">
                          −{formatMoneyPlain(row.credit_applied_lcy)} CM
                        </div>
                      ) : null}
                      {row.due_status === 'waived' ? (
                        <div className="account-collection__desc">
                          Waived by CM
                          {row.credit_applied_lcy > 0
                            ? ` ${formatMoneyPlain(row.credit_applied_lcy)}`
                            : ''}
                        </div>
                      ) : null}
                      {row.due_status === 'applied' ? (
                        <div className="account-collection__desc">
                          Applied to {row.linked_invoice_no || 'invoice'}
                          {row.credit_applied_lcy > 0.005 || row.gross_remaining_lcy > 0.005
                            ? ` −${formatMoneyPlain(row.credit_applied_lcy || row.gross_remaining_lcy)}`
                            : ''}{' '}
                          (not counted again)
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <span
                        className={`account-collection__status account-collection__status--${row.due_status}`}
                      >
                        {statusLabel(row.due_status)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
}
